import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Save,
  X,
  ShoppingBag,
  Key,
  Lock,
  RefreshCw,
  AlertCircle,
  Info,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  get_restaurant_setting,
  save_restaurant_setting,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

let clickTime = 0;

export default function PetPoojaSetting() {
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [isFetchingMenu, setIsFetchingMenu] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    mapping_code: "",
    api_key: "",
    app_secret: "",
    access_token: "",
  });

  const [originalData, setOriginalData] = useState({
    mapping_code: "",
    api_key: "",
    app_secret: "",
    access_token: "",
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Check if form is valid
  const isFormValid =
    formData.mapping_code.trim() !== "" &&
    formData.api_key.trim() !== "" &&
    formData.app_secret.trim() !== "" &&
    formData.access_token.trim() !== "";

  // Check for changes
  useEffect(() => {
    setHasChanges(JSON.stringify(formData) !== JSON.stringify(originalData));
  }, [formData, originalData]);

  // Fetch settings data
  const fetchSettingsData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);
    fd.append("want_table_data", 1);
    fd.append("platform_connect", 1);

    try {
      const response = await server_post_data(get_restaurant_setting, fd);

      if (!response.data.error) {
        if (
          response.data.message.data_platform &&
          response.data.message.data_platform.length > 0
        ) {
          const data = response.data.message.data_platform[0];
          const fetchedData = {
            mapping_code: data.mapping_code || "",
            api_key: data.api_key || "",
            app_secret: data.app_secret || "",
            access_token: data.access_token || "",
          };
          setFormData(fetchedData);
          setOriginalData(fetchedData);
          setHasChanges(false);
        }
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast({
        title: "Error",
        description: "Failed to load PetPooja settings",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.mapping_code.trim()) {
      newErrors.mapping_code = "Mapping code is required";
    }

    if (!formData.api_key.trim()) {
      newErrors.api_key = "API key is required";
    }

    if (!formData.app_secret.trim()) {
      newErrors.app_secret = "App secret is required";
    }

    if (!formData.access_token.trim()) {
      newErrors.access_token = "Access token is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("from_where", 5);
    fd.append("platform_connect", 1);
    Object.keys(formData).forEach((key) => {
      fd.append(key, formData[key]);
    });

    try {
      const response = await server_post_data(save_restaurant_setting, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "PetPooja settings saved successfully",
        });
        fetchSettingsData();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [formData, validateForm, toast, fetchSettingsData]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setFormData(originalData);
    setErrors({});
    setHasChanges(false);
  }, [originalData]);

  // Initial load
  useEffect(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            PetPooja Integration
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure your PetPooja API credentials and sync menu
          </p>
        </div>
      </div>

      {/* Connection Status */}
      {isFormValid && !hasChanges && (
        <Alert className="border-success/50 bg-success/10">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertDescription className="text-success">
            PetPooja integration is configured and active
          </AlertDescription>
        </Alert>
      )}

      {/* API Credentials Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" />
            API Credentials
          </CardTitle>
          <CardDescription>
            Enter your PetPooja API credentials to enable integration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Mapping Code */}
          <div className="space-y-2">
            <Label>
              Mapping Code <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder="Enter mapping code"
              value={formData.mapping_code}
              onChange={(e) =>
                handleFieldChange("mapping_code", e.target.value)
              }
              className={errors.mapping_code ? "border-destructive" : ""}
            />
            {errors.mapping_code && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.mapping_code}
              </p>
            )}
          </div>

          {/* API Key */}
          <div className="space-y-2">
            <Label>
              API Key <span className="text-destructive">*</span>
            </Label>
            <Input
              type="password"
              placeholder="Enter API key"
              value={formData.api_key}
              onChange={(e) => handleFieldChange("api_key", e.target.value)}
              className={errors.api_key ? "border-destructive" : ""}
            />
            {errors.api_key && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.api_key}
              </p>
            )}
          </div>

          {/* App Secret */}
          <div className="space-y-2">
            <Label>
              App Secret <span className="text-destructive">*</span>
            </Label>
            <Input
              type="password"
              placeholder="Enter app secret"
              value={formData.app_secret}
              onChange={(e) => handleFieldChange("app_secret", e.target.value)}
              className={errors.app_secret ? "border-destructive" : ""}
            />
            {errors.app_secret && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.app_secret}
              </p>
            )}
          </div>

          {/* Access Token */}
          <div className="space-y-2">
            <Label>
              Access Token <span className="text-destructive">*</span>
            </Label>
            <Input
              type="password"
              placeholder="Enter access token"
              value={formData.access_token}
              onChange={(e) =>
                handleFieldChange("access_token", e.target.value)
              }
              className={errors.access_token ? "border-destructive" : ""}
            />
            {errors.access_token && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.access_token}
              </p>
            )}
          </div>

          {/* Status Message */}
          {hasChanges && isFormValid && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
              <Lock className="w-4 h-4 text-warning" />
              <p className="text-sm text-warning">
                You have unsaved changes. Click Save to apply.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <ShoppingBag className="w-5 h-5 text-primary" />
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">
                About PetPooja Integration
              </h4>
              <p className="text-sm text-muted-foreground">
                PetPooja integration allows you to sync your menu, orders, and
                inventory with the PetPooja platform. This enables seamless
                order management across multiple channels.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside mt-2">
                <li>Sync menu items and pricing automatically</li>
                <li>Receive orders directly from PetPooja</li>
                <li>Update inventory in real-time</li>
                <li>Manage multiple delivery platforms</li>
              </ul>
              <Alert className="mt-3">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Click "Fetch Menu" after saving credentials to sync your menu
                  with PetPooja
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={showLoaderAdmin || !hasChanges}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={showLoaderAdmin || !hasChanges || !isFormValid}
          className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
        >
          {showLoaderAdmin ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Credentials
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
